import {JSONPath} from 'jsonpath-plus';
import Action from "../../classes/Action";
import {areArraysEqual, JsonCopy} from "../../classes/utilities/Misc";
import {CssStates, IGrapesJSProjectData, IGrapesJSStyle, VisageEditorWindowModes} from "../../interfaces/VisageUI";
import store, {StoreActions} from "../../state/store";
import {IActionValuesChange} from '../../interfaces/Misc';

export interface ISetGrapesProjectActionParams
{
	/** used together with editorMode to find the correct grapes project that needs to be modified */
	grapesProjectId?: string,
	/** which components are affected by this action */
	componentIds?: string[],
	/** the display name of the action in the Action History window */
	actionName?: string,
	/** which css properties should be modified on all components */
	cssPropertyChanges?: IActionValuesChange[],
	/** which attributes should be modified on all components */
	attributeChanges?: IActionValuesChange[],
	/** DANGER! this will directly modify component data without the safety of going through attributes! CAREFUL WITH THIS!!! */
	dataChanges?: IActionValuesChange[],
	/** the mode of the VisageUI editor window, used together with grapesProjectId to find the correct grapes project that needs to be modified */
	editorMode?: VisageEditorWindowModes,
	/** the state of the editor's style manager. limits the changes to the right state when in :hover, :active, etc. states */
	cssState?: CssStates,
}

export default class SetGrapesComponentDataAction extends Action
{
	constructor(params: ISetGrapesProjectActionParams)
	{
		params.editorMode = params.editorMode ?? store.getState().visageModule.editorMode;
		params.cssState = params.cssState ?? store.getState().visageModule.elementState;
		params.grapesProjectId = params.grapesProjectId || store.getState().visageModule.editorContext.projectId;
		params.componentIds = params.componentIds ?? store.getState().visageModule.selectedComponentIds;

		super(
			[params, true],
			[params, false],
			params.actionName || `Set CSS Property`,
			[params],
		);
	}

	/**
	 *
	 *
	 * @param {ISetGrapesProjectActionParams} params
	 * @param {boolean} useNew - if true, the new values are used, otherwise the old values are used. This is used for undoing actions
	 * @memberof SetGrapesComponentDataAction
	 */
	do(params: ISetGrapesProjectActionParams, useNew: boolean)
	{
		if(!params.grapesProjectId || !params.componentIds?.length) return;

		// editing template means actions are temporary, always
		if(params.editorMode === VisageEditorWindowModes.Template)
		{
			this.flagAsTemporary();
		}

		let grapesProject: IGrapesJSProjectData;
		switch(params.editorMode)
		{
			case VisageEditorWindowModes.Step:
				grapesProject = JsonCopy(store.getState().visageModule.stepUIData[params.grapesProjectId]);
				break;
			case VisageEditorWindowModes.Template:
				grapesProject = JsonCopy(store.getState().visageModule.uiTemplateData[params.grapesProjectId].content);
				break;
			default:
				throw new Error("Unsupported editor mode: " + params.editorMode); // technically not required but TS complains
		}

		for(let i = 0;i < params.componentIds.length; i++)
		{
			const componentId = params.componentIds[i];

			// ATTRIBUTES
			if(params.attributeChanges)
			{
				// find the component object
				const query = `$..components[?(@.attributes?.id === "${componentId}")]`;
				const component = JSONPath({ path: query, json: grapesProject })[0];
				if(!component.attributes)
				{
					component.attributes = {};
				}
				
				updateObject(component.attributes, params.attributeChanges, useNew, i);
			}

			// STYLE
			if(params.cssPropertyChanges)
			{
				// find the style object of the component
				const query = `$[?(@.selectors.join('').trim() === "#${componentId}" && (${params.cssState === CssStates.Normal ? "!@.hasOwnProperty('state') ||" : ""} @.state === "${params.cssState}"))].style`;
				const componentStyle = JSONPath({ path: query, json: grapesProject.styles })[0];
				if(componentStyle)
				{
					updateObject(componentStyle, params.cssPropertyChanges, useNew, i);
				}
				else
				{
					const newStyle: IGrapesJSStyle = {
						selectors: [`#${componentId}`],
						state: params.cssState,
						style: {},
					};
					updateObject(newStyle.style, params.cssPropertyChanges, useNew, i);
					grapesProject.styles.push(newStyle);
				}
			}

			// !DANGER! DATA CHANGES !DANGER!
			if(params.dataChanges)
			{
				const query = `$..components[?(@.attributes?.id === "${componentId}")]`;
				const component = JSONPath({ path: query, json: grapesProject })[0];
				if(!component) 
				{
					console.error("Component not found in project: " + componentId);
					continue;
				}

				updateObject(component, params.dataChanges, useNew, i);
			}
		}

		switch(params.editorMode)
		{
			case VisageEditorWindowModes.Step:
				store.dispatch(StoreActions.visage.setStepUIData({ stepId: params.grapesProjectId, data: grapesProject }));
				break;
			case VisageEditorWindowModes.Template:
				const newTemplateData = JsonCopy(store.getState().visageModule.uiTemplateData[params.grapesProjectId]);
				newTemplateData.content = grapesProject;
				store.dispatch(StoreActions.visage.setUITemplateData({ templateId: params.grapesProjectId, data: newTemplateData }));
				break;
		}
	}

	verify(params: ISetGrapesProjectActionParams)
	{
		if(!params.grapesProjectId || !params.componentIds?.length) return false;

		// no project
		let grapesProject: IGrapesJSProjectData;
		switch(params.editorMode)
		{
			case VisageEditorWindowModes.Step:
				grapesProject = store.getState().visageModule.stepUIData[params.grapesProjectId];
				if(!grapesProject) return false;
				break;
			case VisageEditorWindowModes.Template:
				grapesProject = store.getState().visageModule.uiTemplateData[params.grapesProjectId].content;
				if(!grapesProject) return false;
				break;
			default:
				throw new Error("Unsupported editor mode: " + params.editorMode);
		}

		// all components should exist in the project
		for(const componentId of params.componentIds)
		{
			const query = `$..components[?(@.attributes?.id === "${componentId}")]`;
			const component = JSONPath({ path: query, json: grapesProject })[0];
			if(!component) return false;
		}

		// no changes
		if(!params.cssPropertyChanges && !params.attributeChanges && !params.dataChanges) return false;

		// even one attribute changed is enough
		if(params.attributeChanges)
		{
			for(const change of params.attributeChanges)
			{
				if(!areArraysEqual(change.oldValues, change.newValues)) return true;
			}
		}

		// even one css property changed is enough
		if(params.cssPropertyChanges)
		{
			for(const change of params.cssPropertyChanges)
			{
				if(!areArraysEqual(change.oldValues, change.newValues)) return true;
			}
		}

		// even one data change is enough
		if(params.dataChanges)
		{
			for(const change of params.dataChanges)
			{
				if(!areArraysEqual(change.oldValues, change.newValues)) return true;
			}
		}

		return true;
	}
}

function updateObject(obj: Record<string, string>, changes: IActionValuesChange[], newVals: boolean, index: number)
{
	for(const change of changes)
	{
		const valueToSet = newVals ? change.newValues[index] : change.oldValues[index];
		if(valueToSet === "")
		{
			delete obj[change.name];
		}
		else
		{
			obj[change.name] = valueToSet;
		}
	}
}